import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import type { Send } from '../../types/send';
import type { TimePeriod } from '../../utils/stats-utils';
import { getSendCategory, groupByPeriod, sortedPeriodKeys, periodLabel, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

const COLORS = {
  on_sight: '#22c55e',
  flash: '#f97316',
  rotpunk: '#3b82f6',
};

interface VolumeOverTimeProps {
  sends: Send[];
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}

export default function VolumeOverTime({ sends, period, onPeriodChange }: VolumeOverTimeProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const groups = groupByPeriod(sends, period);
    const keys = sortedPeriodKeys(groups);
    return keys.map(key => {
      const bucket = { on_sight: 0, flash: 0, rotpunk: 0 };
      for (const send of groups[key]) {
        bucket[getSendCategory(send.attributes.send_type)]++;
      }
      return { label: periodLabel(key, period), ...bucket };
    });
  }, [sends, period]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t('stats.volumeOverTime.title')}</CardTitle>
        <ToggleGroup
          type="single"
          value={period}
          onValueChange={(v) => { if (v) onPeriodChange(v as TimePeriod); }}
          className="h-7"
        >
          <ToggleGroupItem value="week" className="h-7 px-2 text-xs">{t('stats.period.week')}</ToggleGroupItem>
          <ToggleGroupItem value="month" className="h-7 px-2 text-xs">{t('stats.period.month')}</ToggleGroupItem>
          <ToggleGroupItem value="year" className="h-7 px-2 text-xs">{t('stats.period.year')}</ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      {data.length === 0 ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend
                  formatter={(value) => t(`stats.categories.${value}`)}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="on_sight" name="on_sight" stackId="a" fill={COLORS.on_sight} />
                <Bar dataKey="flash" name="flash" stackId="a" fill={COLORS.flash} />
                <Bar dataKey="rotpunk" name="rotpunk" stackId="a" fill={COLORS.rotpunk} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
